import { useEffect, useRef } from 'react';

/**
 * Make an element scrollable by dragging
 * @param buttons - Buttons user can drag with. See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
 * @returns {React.MutableRefObject} -  The ref to be applied to to the element to make it draggable
 */
export default function useDraggable(buttons = [1, 4, 5]) {
  // Ref to be attached to the element we want to drag
  const ref = useRef();
  // Use refs for position values to avoid re-renders and stale closures
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const startScrollTopRef = useRef(0);

  useEffect(() => {
    function handleDown(e) {
      // Only allow dragging inside of target element
      if (!ref.current?.contains(e.target)) {
        return;
      }
      // Set initial positions of mouse element scroll
      startXRef.current = e.pageX - ref.current.offsetLeft;
      startYRef.current = e.pageY - ref.current.offsetTop;
      startScrollLeftRef.current = ref.current.scrollLeft;
      startScrollTopRef.current = ref.current.scrollTop;
    }
    function handleMove(e) {
      // Don't fire if other buttons are pressed
      if (!buttons.includes(e.buttons) || !ref.current?.contains(e.target)) {
        return;
      }
      e.preventDefault();
      // Position of mouse on the page
      const mouseX = e.pageX - ref.current.offsetLeft;
      const mouseY = e.pageY - ref.current.offsetTop;
      // Distance of the mouse from the origin of the last mousedown event
      const walkX = mouseX - startXRef.current;
      const walkY = mouseY - startYRef.current;
      // Set element scroll
      ref.current.scrollLeft = startScrollLeftRef.current - walkX;
      ref.current.scrollTop = startScrollTopRef.current - walkY;
    }
    // Add and clean up listeners
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('mousemove', handleMove);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('mousemove', handleMove);
    };
  }, [buttons]);

  return ref;
}
